import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Bot, User, SquarePen, Info, ArrowRight, Menu } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ArcheLayout } from '../../components/ArcheLayout';
import { useSearchResults } from '../../context/SearchResultsContext';
import { useSidebar } from '../../context/SidebarContext';
import { api, MarketplaceArche } from '@/lib/api';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

// Card component for displaying top search results
function ArcheResultCard({ arche, onClick }: { arche: MarketplaceArche; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-full p-4 rounded-xl bg-card border border-border hover:border-primary/50 hover:bg-accent/50 transition-all text-left group"
    >
      <div className="flex items-start gap-3">
        {arche.imageRef ? (
          <img
            src={arche.imageRef}
            alt={arche.name}
            className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <span className="text-xl font-semibold text-primary">{arche.name.charAt(0)}</span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-foreground truncate">{arche.name}</h3>
            <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
          </div>
          <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
            {arche.simpleDescription}
          </p>
        </div>
      </div>
    </button>
  );
}

// Inner component that uses the SearchResults context
function MakeARequestContent() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isSearching, setIsSearchingLocal] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { searchResults, setSearchResults, setIsSearching, clearSearchResults } =
    useSearchResults();
  const { isCollapsed, isMobile, toggle, setCollapsed } = useSidebar();

  // Open sidebar by default on desktop for first-time users exploring the system
  useEffect(() => {
    if (!isMobile) {
      setCollapsed(false);
    }
  }, [isMobile, setCollapsed]);

  // Get top 3 results for display in main content area
  const topResults = searchResults.slice(0, 3);

  // Reset conversation
  const handleReset = useCallback(() => {
    setMessages([]);
    setInputValue('');
    clearSearchResults();
    if (!isMobile) {
      textareaRef.current?.focus();
    }
  }, [clearSearchResults, isMobile]);

  // Auto-scroll to bottom when messages change
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus textarea when not searching (skip on mobile)
  useEffect(() => {
    if (!isSearching && !isMobile) {
      textareaRef.current?.focus();
    }
  }, [isSearching, isMobile]);

  // Auto-resize textarea based on content
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue, adjustTextareaHeight]);

  // Handle search for arches
  const handleSearch = async (query: string) => {
    const userMessageId = `user-${Date.now()}`;
    const assistantMessageId = `assistant-${Date.now()}`;

    const userMessage: Message = {
      id: userMessageId,
      role: 'user',
      content: query,
    };

    const assistantMessage: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: 'View relevant arches on the left.',
    };

    // Clear previous messages and start fresh search
    setMessages([userMessage, assistantMessage]);
    setInputValue('');
    setIsSearchingLocal(true);
    setIsSearching(true);

    try {
      const result = await api.searchMarketplace({ q: query, limit: 10 });
      setSearchResults(result.arches, query);
    } catch (err) {
      console.error('Search failed:', err);
      setSearchResults([], query);
    } finally {
      setIsSearchingLocal(false);
      setIsSearching(false);
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isSearching) {
      return;
    }

    await handleSearch(trimmedInput);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {/* Sidebar toggle (always rendered to maintain layout, hidden when open) */}
            <Button
              variant="outline"
              size="icon"
              onClick={toggle}
              className={`h-11 w-11 flex-shrink-0 bg-gray-100 dark:bg-zinc-700 ${isCollapsed ? '' : 'invisible'}`}
              title="Open sidebar"
            >
              <Menu className="h-6 w-6" />
            </Button>
            <h1 className="text-lg font-semibold">Make a Re-Quest™</h1>
            <Link
              to="/marketplace/arches/arche_make_a_request"
              className="text-muted-foreground hover:text-primary transition-colors"
              title="View arche details"
            >
              <Info className="h-6 w-6" />
            </Link>
          </div>
          <div className="flex items-center gap-4">
            {/* Favorites toggle */}
            <Button variant="outline" size="sm" onClick={() => navigate('/a/arche_favorites')}>
              My Favorites
            </Button>
            {/* New search button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={isSearching || messages.length === 0}
              className="gap-1.5 [&_svg]:size-5!"
            >
              <SquarePen />
              New
            </Button>
          </div>
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-6">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center">
              <img
                src="/fias-logo-image.png"
                alt="Fias AI"
                className="h-16 w-16 mb-4 dark:invert dark:brightness-90"
              />
              <h2 className="text-xl font-medium text-foreground mb-2">Make a Re-Quest™</h2>
              <p className="text-muted-foreground max-w-md">
                Ask anything to make a Re-Quest to Fias AI, and find an Arche to help you solve a
                problem!
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Show user's search query */}
              {messages
                .filter((m) => m.role === 'user')
                .map((message) => (
                  <div key={message.id} className="flex gap-4 justify-end">
                    <div className="max-w-[80%] rounded-2xl px-4 py-3 bg-primary text-primary-foreground">
                      <div className="whitespace-pre-wrap break-words">{message.content}</div>
                    </div>
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                      <User className="h-4 w-4 text-primary-foreground" />
                    </div>
                  </div>
                ))}

              {/* Show search results */}
              {topResults.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Fias AI</div>
                      <h3 className="text-lg font-medium">
                        Here are top matches for your Re-Quest
                      </h3>
                    </div>
                  </div>
                  <div className="ml-11 space-y-3">
                    {topResults.map((arche) => (
                      <ArcheResultCard
                        key={arche.archId}
                        arche={arche}
                        onClick={() => navigate(`/a/${arche.archId}`)}
                      />
                    ))}
                    {searchResults.length > 3 && (
                      <p className="text-sm text-muted-foreground mt-2">
                        +{searchResults.length - 3} more results in the sidebar
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* No results message */}
              {messages.length > 0 && topResults.length === 0 && !isSearching && (
                <div className="flex gap-4 justify-start">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Bot className="h-4 w-4 text-primary" />
                  </div>
                  <div className="bg-muted rounded-2xl px-4 py-3">
                    <div className="text-xs text-muted-foreground mb-1">Fias AI</div>
                    <p>
                      No arches found matching your request. Try a different search or browse the
                      Arche Store.
                    </p>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>

      {/* Input area */}
      <div className="flex-shrink-0 border-t border-border bg-background">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <form onSubmit={handleSubmit} className="relative">
            <Textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="What is your Re-Quest?"
              disabled={isSearching}
              className="min-h-[44px] max-h-[200px] pr-14 resize-none rounded-2xl bg-muted/50 overflow-y-auto"
              rows={1}
              style={{ height: 'auto' }}
            />
            <Button
              type="submit"
              size="icon"
              disabled={!inputValue.trim() || isSearching}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-xl"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
          <p className="text-xs text-muted-foreground text-center mt-2">
            Press Enter to search, Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>
  );
}

// Outer component that wraps with ArcheLayout (which provides SearchResultsProvider)
export function MakeARequestPage() {
  return (
    <ArcheLayout>
      <MakeARequestContent />
    </ArcheLayout>
  );
}

export default MakeARequestPage;
